<template>
  <b-modal size="xl" v-model="show" :title="$t('exportContractTitle')" no-close-on-backdrop no-enforce-focus>
    <div v-if="loading" class="text-center py-4">
      <b-spinner small></b-spinner> {{ $t('loading') }}
    </div>
    <div v-else-if="!kyc">
      <div class="text-center py-4 text-danger">{{ $t('kycNotFound') }}</div>
    </div>
    <div v-else-if="!templates.length" class="text-center py-4 text-muted">
      <i class="fa fa-file-excel fa-2x mb-2 d-block"></i>
      {{ $t('noTemplates') }}
    </div>
    <div v-else>
      <!-- ============== 1. THÔNG TIN KHÁCH (full width, lên đầu) ============== -->
      <fieldset class="kyc-section mb-3">
        <legend class="kyc-legend"><i class="fa fa-info-circle mr-1"></i>{{ $t('clientInfo') }}</legend>
        <div class="row">
          <div class="col-md-6">
            <div class="mb-1">
              <span :class="kyc.type === 'enterprise' ? 'badge badge-primary' : 'badge badge-secondary'">
                {{ $t(kyc.type) }}
              </span>
              <b class="ml-2">{{ kyc.client && kyc.client.companyName }}</b>
              <code v-if="kyc.client" class="ml-2">{{ kyc.client.domain }}</code>
            </div>
            <div v-if="kyc.type === 'enterprise'" class="mb-1">
              <i class="fa fa-building text-muted mr-1"></i>
              <b>{{ kyc.companyName || '—' }}</b>
              <span v-if="kyc.shortName" class="text-muted ml-1">({{ kyc.shortName }})</span>
              <span v-if="kyc.companyTaxCode" class="text-muted ml-2">
                <i class="fa fa-receipt"></i> {{ kyc.companyTaxCode }}
              </span>
            </div>
            <div class="mb-1">
              <i class="fa fa-user text-muted mr-1"></i>
              {{ kyc.name || '—' }}
              <span v-if="kyc.position" class="text-muted">— {{ kyc.position }}</span>
            </div>
          </div>
          <div class="col-md-6">
            <div v-if="defaults.latestStartTime">
              <div class="mb-1">
                <i class="fa fa-calendar-check text-success mr-1"></i>
                <b>{{ $t('latestOrder') }}:</b>
                {{ defaults.latestStartTime | moment('DD/MM/YYYY') }}
                <span v-if="defaults.latestMonthCount" class="text-muted">
                  — {{ defaults.latestMonthCount }} {{ $t('months') }}
                </span>
              </div>
              <div class="mb-1" v-if="defaults.latestLimit">
                <i class="fa fa-users text-muted mr-1"></i>
                {{ defaults.latestLimit }} {{ $t('users') }}
                <span v-if="defaults.gia_user" class="text-muted ml-2">— {{ defaults.gia_user }}đ/user</span>
              </div>
              <div v-if="defaults.latestTotal" class="text-primary">
                <i class="fa fa-money-bill mr-1"></i>
                <b>{{ formatVND(defaults.latestTotal) }}</b>
                <span v-if="defaults.ten_goi_dv" class="text-muted ml-2">— {{ defaults.ten_goi_dv }}</span>
              </div>
            </div>
            <div v-else class="text-muted">
              <i class="fa fa-shopping-bag mr-1"></i>{{ $t('noOrderYet') }}
            </div>
          </div>
        </div>
      </fieldset>

      <!-- ============== 2. CẤU HÌNH XUẤT ============== -->
      <fieldset class="kyc-section mb-3">
        <legend class="kyc-legend"><i class="fa fa-cog mr-1"></i>{{ $t('exportConfig') }}</legend>
        <div class="row">
          <div class="col-md-5">
            <b-form-group class="mb-2">
              <template v-slot:label>
                <b>{{ $t('contractNumber') }}</b><span class="text-danger">&nbsp;*</span>
                <button v-if="autoContractNumber && contractNumber !== autoContractNumber"
                        type="button"
                        class="btn btn-link btn-sm py-0 ml-2"
                        @click="contractNumber = autoContractNumber"
                        :title="$t('useAutoNumber')">
                  <i class="fa fa-magic"></i> {{ $t('useAutoNumber') }}
                </button>
              </template>
              <b-form-input
                v-model="contractNumber"
                :placeholder="$t('contractNumberPlaceholder')"
                maxlength="100"></b-form-input>
              <small class="form-text text-muted">{{ $t('contractNumberHint') }}</small>
            </b-form-group>
          </div>
          <div class="col-md-4">
            <b-form-group class="mb-2">
              <template v-slot:label><b>{{ $t('selectTemplate') }}</b></template>
              <b-form-select v-model="selectedId" :options="templateOptions"></b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-3">
            <b-form-group class="mb-2">
              <template v-slot:label><b>{{ $t('format') }}</b></template>
              <b-form-radio-group v-model="format" stacked>
                <b-form-radio value="pdf">
                  <i class="fa fa-file-pdf text-danger mr-1"></i>{{ $t('formatPdf') }}
                </b-form-radio>
                <b-form-radio value="docx">
                  <i class="fa fa-file-word text-primary mr-1"></i>{{ $t('formatDocx') }}
                </b-form-radio>
              </b-form-radio-group>
            </b-form-group>
          </div>
        </div>
      </fieldset>

      <!-- Banner thiếu thông tin -->
      <div v-if="selectedTpl && missingAutoFields.length" class="alert alert-warning py-2 mb-3">
        <i class="fa fa-exclamation-triangle mr-1"></i>
        <strong>{{ $t('missingAutoFields', [missingAutoFields.length]) }}</strong>
        — {{ $t('missingAutoFieldsHint') }}
        <ul class="mb-0 mt-1">
          <li v-for="(label, idx) in missingAutoFields" :key="idx">{{ label }}</li>
        </ul>
      </div>

      <!-- ============== 3. AUTO + MANUAL FIELDS (2 cột) ============== -->
      <div v-if="selectedTpl" class="row">
        <!-- TRÁI: Auto-fill -->
        <div class="col-lg-5">
          <fieldset class="kyc-section h-100">
            <legend class="kyc-legend">
              <i class="fa fa-magic mr-1"></i>{{ $t('previewSection') }}
              <span class="badge badge-light text-muted">{{ autoVariables.length }}</span>
            </legend>
            <p class="text-muted mb-2">{{ $t('previewSectionHint') }}</p>
            <dl class="row mb-0">
              <template v-for="v in autoVariables">
                <dt :key="v.key + '-l'" class="col-5 text-muted font-weight-normal">{{ v.label }}</dt>
                <dd :key="v.key + '-v'" class="col-7">
                  <span v-if="autoValues[v.key]">{{ autoValues[v.key] }}</span>
                  <span v-else class="text-warning">
                    <i class="fa fa-exclamation-triangle"></i> {{ $t('empty') }}
                    <span v-if="v.required" class="badge badge-danger ml-1">{{ $t('required') }}</span>
                  </span>
                </dd>
              </template>
            </dl>
          </fieldset>
        </div>

        <!-- PHẢI: Manual fields -->
        <div class="col-lg-7">
          <fieldset class="kyc-section h-100">
            <legend class="kyc-legend">
              <i class="fa fa-edit mr-1"></i>{{ $t('manualSection') }}
              <span class="badge badge-light text-muted">{{ manualVariables.length }}</span>
            </legend>
            <div v-if="!manualVariables.length" class="text-center text-muted py-3">
              <i class="fa fa-check-circle text-success mr-1"></i>{{ $t('noManualNeeded') }}
            </div>
            <template v-else>
              <p class="text-muted mb-2">{{ $t('manualSectionHint') }}</p>
              <div class="row">
                <div v-for="v in manualVariables" :key="v.key" class="col-md-6 mb-2">
                  <label class="font-weight-bold mb-1">
                    {{ v.label }}<span v-if="v.required" class="text-danger">&nbsp;*</span>
                  </label>
                  <date-picker
                    v-if="v.type === 'date'"
                    :value="manualData[v.key] || ''"
                    value-type="format"
                    format="DD/MM/YYYY"
                    placeholder="dd/mm/yyyy"
                    input-class="form-control"
                    style="width:100%"
                    @input="val => $set(manualData, v.key, val || '')"></date-picker>
                  <b-form-input
                    v-else
                    v-model="manualData[v.key]"
                    :placeholder="v.description || v.defaultValue || ''"
                    maxlength="200"></b-form-input>
                </div>
              </div>
            </template>
          </fieldset>
        </div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <div class="d-flex w-100 justify-content-between align-items-center">
        <button class="btn btn-secondary" @click="show = false" :disabled="exporting">
          {{ $t('cancel') }}
        </button>
        <button class="btn btn-primary" @click="onExport" :disabled="!selectedTpl || exporting">
          <b-spinner v-if="exporting" small></b-spinner>
          <i v-else class="fa fa-download mr-1"></i>
          {{ $t('exportSubmit') }}
        </button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
  components: {DatePicker},
  props: {
    kycRequestId: {type: String, default: null},
  },
  data() {
    return {
      show: false,
      loading: false,
      kyc: null,
      templates: [],
      selectedId: null,
      manualData: {},
      defaults: {},
      contractNumber: '',
      contractNumberManuallyEdited: false,
      format: 'pdf',
      exporting: false,
    };
  },
  computed: {
    templateOptions() {
      return [
        {value: null, text: this.$t('selectTemplatePlaceholder')},
        ...this.templates.map(t => ({value: t.id, text: t.name})),
      ];
    },
    selectedTpl() {
      return this.templates.find(t => t.id === this.selectedId) || null;
    },
    autoVariables() {
      return (this.selectedTpl && this.selectedTpl.variables || []).filter(v => v.source !== 'manual');
    },
    manualVariables() {
      // Bỏ field hidden (đã có default tự sinh từ BE — admin không cần nhập)
      return (this.selectedTpl && this.selectedTpl.variables || [])
          .filter(v => v.source === 'manual' && !v.hidden);
    },
    autoValues() {
      const out = {};
      if (!this.selectedTpl || !this.kyc) return out;
      for (const v of this.autoVariables) {
        out[v.key] = this.resolveAutoValue(v.source);
      }
      return out;
    },
    missingAutoFields() {
      if (!this.selectedTpl) return [];
      return this.autoVariables
          .filter(v => v.required && !this.autoValues[v.key])
          .map(v => v.label);
    },
    missingManualFields() {
      if (!this.selectedTpl) return [];
      // Chỉ kiểm tra field admin thấy (visible). Hidden field đã có default từ BE
      return this.manualVariables
          .filter(v => v.required && (!this.manualData[v.key] || !String(this.manualData[v.key]).trim()))
          .map(v => v.label);
    },
    allMissingFields() {
      return [...this.missingAutoFields, ...this.missingManualFields];
    },
    /**
     * Auto-generate số HĐ theo quy định:
     * Pattern: DDMM-HDDV/{TENVIETTAT}-CALLIO/YYYY
     * VD: 0205-HDDV/NGOCLANVILLAS-CALLIO/2026
     */
    autoContractNumber() {
      if (!this.kyc || !this.defaults || !this.defaults.latestStartTime) return '';
      const d = new Date(this.defaults.latestStartTime);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      // Ưu tiên shortName, fallback companyName, fallback name (cho personal)
      const rawName = this.kyc.shortName || this.kyc.companyName || this.kyc.name || '';
      const slug = this.slugify(rawName);
      if (!slug) return '';
      return `${dd}${mm}-HDDV/${slug}-CALLIO/${yyyy}`;
    },
  },
  watch: {
    selectedId() {
      this.manualData = {};
      for (const v of this.manualVariables) {
        const fromDB = this.defaults[v.key];
        if (fromDB !== undefined && fromDB !== '' && fromDB !== null) {
          this.$set(this.manualData, v.key, String(fromDB));
        } else if (v.defaultValue) {
          this.$set(this.manualData, v.key, v.defaultValue);
        }
      }
    },
    contractNumber(val, oldVal) {
      // Track admin đã sửa thủ công
      if (oldVal !== '' && val !== this.autoContractNumber) {
        this.contractNumberManuallyEdited = true;
      }
    },
  },
  methods: {
    async open(kycId) {
      this.show = true;
      this.loading = true;
      this.kyc = null;
      this.selectedId = null;
      this.manualData = {};
      this.defaults = {};
      this.contractNumber = '';
      this.contractNumberManuallyEdited = false;
      this.format = 'pdf';

      try {
        const kycRes = await this.$service.get(`/client-kyc/${kycId}?populateClient=true`);
        this.kyc = kycRes.data;

        if (!this.kyc.type) {
          this.$swal({text: this.$t('kycTypeRequired'), icon: 'warning'});
          this.show = false;
          return;
        }

        const [tplRes, defRes] = await Promise.all([
          this.$service.get('/client-kyc/contract-templates', {params: {type: this.kyc.type}}),
          this.$service.get(`/client-kyc/${kycId}/contract-defaults`),
        ]);
        this.templates = tplRes.data;
        this.defaults = defRes.data || {};

        // Số HĐ: ưu tiên đã lưu trong DB, fallback auto-generate
        this.contractNumber = this.defaults.contractNumber || this.autoContractNumber || '';

        if (this.templates.length === 1) this.selectedId = this.templates[0].id;
      } catch (e) {
        this.$swal({
          text: (e.response && e.response.data && e.response.data.message) || e.message,
          icon: 'error',
        });
        this.show = false;
      } finally {
        this.loading = false;
      }
    },
    async onExport() {
      if (!this.contractNumber || !this.contractNumber.trim()) {
        this.$swal({text: this.$t('contractNumberRequired'), icon: 'warning'});
        return;
      }
      // Pre-validate required fields (cả auto + manual) trước khi gọi BE
      if (this.allMissingFields.length) {
        this.$swal({
          title: this.$t('exportFailedTitle'),
          html: this.$t('preValidateMissing') + '<ul style="text-align:left;margin-top:8px">'
              + this.allMissingFields.map(v => '<li>' + v + '</li>').join('')
              + '</ul>',
          icon: 'warning',
        });
        return;
      }
      this.exporting = true;
      try {
        const response = await this.$service.post(
            `/client-kyc/${this.kyc._id}/export-contract`,
            {
              templateId: this.selectedId,
              format: this.format,
              manualData: this.manualData,
              contractNumber: this.contractNumber.trim(),
            },
            {responseType: 'blob'}
        );
        const isFallbackToDocx = response.headers['x-pdf-fallback'] === '1';
        const actualFormat = (this.format === 'pdf' && !isFallbackToDocx) ? 'pdf' : 'docx';
        const fallbackType = actualFormat === 'pdf'
            ? 'application/pdf'
            : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        const blob = new Blob([response.data], {type: response.headers['content-type'] || fallbackType});
        const fileName = this.parseFileName(response.headers['content-disposition']) || `contract.${actualFormat}`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 100);

        if (response.headers['x-pdf-fallback'] === '1') {
          this.$swal({text: this.$t('pdfFallbackWarn'), icon: 'warning', toast: true, timer: 5000, position: 'top-end'});
        } else {
          this.$swal({text: this.$t('exportSuccess'), icon: 'success', toast: true, timer: 3000, position: 'top-end'});
        }

        // Nếu mở từ KYCRequestDetail → auto-mark exported
        if (this.kycRequestId) {
          try {
            await this.$service.post(`/kyc-request/${this.kycRequestId}/mark-exported`, {
              contractNumber: this.contractNumber.trim(),
              kyc: this.kyc._id,
            });
            this.$emit('exported', {contractNumber: this.contractNumber.trim(), kyc: this.kyc._id});
          } catch (markErr) {
            console.error('mark-exported fail (non-blocking)', markErr);
          }
        }

        this.show = false;
      } catch (e) {
        let msg = e.message;
        let missingVars = null;
        // Response là Blob khi error (vì FE request responseType:'blob')
        if (e.response && e.response.data) {
          if (e.response.data instanceof Blob) {
            try {
              const text = await e.response.data.text();
              try {
                const json = JSON.parse(text);
                msg = json.message || msg;
                missingVars = json.missingVars || null;
              } catch (_) {
                // Response là plain text — dùng nguyên text
                if (text) msg = text;
              }
            } catch (_) { /* ignore blob read error */ }
          } else if (typeof e.response.data === 'object') {
            msg = e.response.data.message || msg;
            missingVars = e.response.data.missingVars || null;
          } else if (typeof e.response.data === 'string') {
            msg = e.response.data;
          }
        }
        // Hiển thị message + list missingVars nếu có
        if (missingVars && missingVars.length) {
          this.$swal({
            title: this.$t('exportFailedTitle'),
            html: msg + '<ul style="text-align:left;margin-top:8px">'
                + missingVars.map(v => '<li>' + v + '</li>').join('')
                + '</ul>',
            icon: 'warning',
          });
        } else {
          this.$swal({title: this.$t('exportFailedTitle'), text: msg, icon: 'error'});
        }
      } finally {
        this.exporting = false;
      }
    },
    resolveAutoValue(source) {
      if (!source) return '';
      if (source.indexOf('||') !== -1) {
        const sources = source.split('||').map(s => s.trim()).filter(Boolean);
        for (const s of sources) {
          const v = this.resolveAutoValue(s);
          if (v) return v;
        }
        return '';
      }
      if (source === 'system.today') return moment().format('DD/MM/YYYY');
      if (source === 'system.day') return moment().format('DD');
      if (source === 'system.month') return moment().format('MM');
      if (source === 'system.year') return moment().format('YYYY');
      if (source === 'system.exporter') return (this.$store.state.user && this.$store.state.user.name) || '';
      if (source === 'system.exportTime') return moment().format('HH:mm DD/MM/YYYY');
      if (source.startsWith('kyc.')) return _.get(this.kyc, source.slice(4)) || '';
      if (source.startsWith('client.')) return _.get(this.kyc && this.kyc.client, source.slice(7)) || '';
      return '';
    },
    /**
     * Convert "DD/MM/YYYY" → "YYYY-MM-DD" cho HTML5 date input value.
     * Trả '' nếu input không hợp lệ → input rỗng cho admin chọn.
     */
    toDateInputValue(ddmmyyyy) {
      if (!ddmmyyyy) return '';
      const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(ddmmyyyy).trim());
      if (!m) return '';
      return `${m[3]}-${m[2]}-${m[1]}`;
    },
    /**
     * HTML5 date picker emits "YYYY-MM-DD". Lưu lại manualData dưới dạng "DD/MM/YYYY".
     */
    onDateInput(key, isoValue) {
      if (!isoValue) {
        this.$set(this.manualData, key, '');
        return;
      }
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoValue);
      this.$set(this.manualData, key, m ? `${m[3]}/${m[2]}/${m[1]}` : '');
    },
    /**
     * Slugify tiếng Việt: bỏ dấu, bỏ space, uppercase, giữ alphanumeric.
     * "Công ty TNHH NGOC LAN VILLAS" → "CONGTYTNHHNGOCLANVILLAS"
     */
    slugify(str) {
      return String(str || '')
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .replace(/đ/g, 'd').replace(/Đ/g, 'D')
          .replace(/[^a-zA-Z0-9]/g, '')
          .toUpperCase();
    },
    formatVND(num) {
      if (num == null || isNaN(num)) return '';
      return Number(num).toLocaleString('vi-VN') + ' đ';
    },
    parseFileName(contentDisposition) {
      if (!contentDisposition) return null;
      const m = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
      if (m) {
        try {
          return decodeURIComponent(m[1]);
        } catch (_) {
          return m[1];
        }
      }
      const m2 = /filename="?([^";]+)"?/i.exec(contentDisposition);
      return m2 ? m2[1] : null;
    },
  },
  i18n: {
    messages: {
      en: {
        exportContractTitle: 'Export contract from template',
        loading: 'Loading...',
        kycNotFound: 'KYC not found',
        noTemplates: 'No template available for this KYC type',
        kycTypeRequired: 'Please classify KYC (Personal/Enterprise) before exporting',
        clientInfo: 'Client info',
        latestOrder: 'Latest order',
        months: 'months',
        users: 'users',
        noOrderYet: 'No order yet',
        exportConfig: 'Export configuration',
        contractNumber: 'Contract number',
        contractNumberPlaceholder: 'Enter contract number',
        contractNumberHint: 'Will be saved to KYC for reuse',
        useAutoNumber: 'Use auto',
        contractNumberRequired: 'Please enter contract number',
        selectTemplate: 'Template',
        selectTemplatePlaceholder: '-- Select template --',
        previewSection: 'Auto-filled from data',
        previewSectionHint: 'Read-only. To edit, update KYC profile.',
        manualSection: 'Manual input',
        manualSectionHint: 'Pre-filled where possible. Edit before exporting.',
        noManualNeeded: 'No manual input required',
        missingAutoFields: 'Missing {0} required field(s) in KYC',
        missingAutoFieldsHint: 'Update KYC to fill these',
        empty: '(missing)',
        required: 'required',
        format: 'Format',
        formatPdf: 'PDF',
        formatDocx: 'DOCX',
        cancel: 'Cancel',
        exportSubmit: 'Export contract',
        exportSuccess: 'Contract exported successfully',
        exportFailedTitle: 'Export failed',
        preValidateMissing: 'Please fill in the following required fields:',
        pdfFallbackWarn: 'PDF conversion failed, DOCX downloaded instead.',
        personal: 'Personal',
        enterprise: 'Enterprise',
      },
      vi: {
        exportContractTitle: 'Xuất hợp đồng từ mẫu',
        loading: 'Đang tải...',
        kycNotFound: 'Không tìm thấy KYC',
        noTemplates: 'Chưa có mẫu phù hợp với loại KYC này',
        kycTypeRequired: 'Vui lòng phân loại KYC (Cá nhân/Doanh nghiệp) trước khi xuất',
        clientInfo: 'Thông tin khách hàng',
        latestOrder: 'Đơn gần nhất',
        months: 'tháng',
        users: 'user',
        noOrderYet: 'Chưa có đơn hàng nào',
        exportConfig: 'Cấu hình xuất',
        contractNumber: 'Số hợp đồng',
        contractNumberPlaceholder: 'Nhập số hợp đồng',
        contractNumberHint: 'Sẽ lưu vào KYC để dùng cho lần xuất sau',
        useAutoNumber: 'Dùng số tự sinh',
        contractNumberRequired: 'Vui lòng nhập số hợp đồng',
        selectTemplate: 'Mẫu hợp đồng',
        selectTemplatePlaceholder: '-- Chọn mẫu --',
        previewSection: 'Tự động điền',
        previewSectionHint: 'Read-only. Muốn sửa, vào hồ sơ KYC cập nhật.',
        manualSection: 'Nhập tay',
        manualSectionHint: 'Đã prefill từ data có sẵn. Sửa trước khi xuất nếu cần.',
        noManualNeeded: 'Mẫu này không cần nhập tay',
        missingAutoFields: 'Thiếu {0} thông tin bắt buộc trong KYC',
        missingAutoFieldsHint: 'Cập nhật KYC để điền',
        empty: '(thiếu)',
        required: 'bắt buộc',
        format: 'Định dạng',
        formatPdf: 'PDF',
        formatDocx: 'DOCX',
        cancel: 'Huỷ',
        exportSubmit: 'Xuất hợp đồng',
        exportSuccess: 'Đã xuất hợp đồng',
        exportFailedTitle: 'Xuất hợp đồng thất bại',
        preValidateMissing: 'Vui lòng điền đầy đủ các trường bắt buộc sau:',
        pdfFallbackWarn: 'Không convert được PDF, đã tải file DOCX.',
        personal: 'Cá nhân',
        enterprise: 'Doanh nghiệp',
      },
    },
  },
};
</script>

<style scoped>
.kyc-section {
  border: 1px solid #e3e6f0;
  border-radius: 6px;
  padding: 12px 16px;
  background: #fff;
}

.kyc-legend {
  width: auto;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #5a5c69;
  padding: 2px 8px;
  margin-bottom: 8px;
  background: #f8f9fc;
  border-radius: 4px;
  border: 1px solid #e3e6f0;
}

.kyc-section dl.row dt {
  font-weight: 500;
  margin-bottom: 6px;
}

.kyc-section dl.row dd {
  margin-bottom: 6px;
  word-break: break-word;
}
</style>
